import { useId, useRef, useState } from 'react';
import { ACCEPT_INPUT_ATTR, MAX_FILE_SIZE_BYTES } from '../../lib/files';

export type DropZonePhase = 'idle' | 'extracting' | 'success' | 'error';

export interface DropZoneDocInfo {
  name: string;
  chars: number;
}

interface DropZoneProps {
  phase: DropZonePhase;
  message: string;
  docInfo: DropZoneDocInfo | null;
  onFileSelected: (file: File) => void;
  onRemoveDocument: () => void;
}

const MESSAGE_TONE: Record<DropZonePhase, string> = {
  idle: 'text-ink-faint',
  extracting: 'text-ink-soft',
  success: 'text-verified',
  error: 'text-danger',
};

/**
 * Upload affordance for the analyzer. Supports click, keyboard, and drag/drop
 * while keeping a real `input[type=file]` for tests and assistive tech.
 */
export function DropZone({
  phase,
  message,
  docInfo,
  onFileSelected,
  onRemoveDocument,
}: DropZoneProps) {
  const inputId = useId();
  const hintId = useId();
  const busy = phase === 'extracting';
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const pick = (file: File | undefined) => {
    if (file) onFileSelected(file);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          depth.current += 1;
          if (!busy) setDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          depth.current -= 1;
          if (depth.current <= 0) {
            depth.current = 0;
            setDragging(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          depth.current = 0;
          setDragging(false);
          if (busy) return;
          pick(e.dataTransfer.files?.[0]);
        }}
        className={[
          'rounded-lg border border-dashed p-4 transition-colors duration-200 ease-out',
          dragging
            ? 'border-accent bg-accent-soft/60'
            : 'border-line-strong bg-surface-sunken/70 hover:border-navy/40',
        ].join(' ')}
      >
        <input
          id={inputId}
          type="file"
          className="peer sr-only"
          accept={ACCEPT_INPUT_ATTR}
          disabled={busy}
          aria-describedby={hintId}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset first so re-selecting the same file still fires change.
            event.target.value = '';
            pick(file);
          }}
        />

        <div className="flex flex-wrap items-center gap-3">
          <label
            htmlFor={inputId}
            className="peer-focus-visible:shadow-focus inline-flex cursor-pointer items-center gap-2 rounded-pill border border-line-strong bg-surface px-4 py-2 text-sm font-semibold text-ink shadow-card transition-colors duration-200 ease-out hover:bg-surface-sunken peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
          >
            <UploadGlyph busy={busy} />
            {busy ? 'Reading document…' : 'Upload CV'}
          </label>
          <p className="text-xs text-ink-faint">
            or drag a file onto this panel
          </p>
        </div>

        {docInfo ? (
          <div className="mt-3 flex min-w-0 items-center gap-2 rounded-pill border border-accent/20 bg-accent-soft px-3 py-1.5 text-sm text-navy">
            <span className="truncate font-medium" title={docInfo.name}>
              {docInfo.name}
            </span>
            <span className="shrink-0 text-xs text-ink-soft">
              {docInfo.chars.toLocaleString('en-US')} chars
            </span>
            <button
              type="button"
              onClick={onRemoveDocument}
              aria-label={`Remove document ${docInfo.name}`}
              className="shrink-0 rounded-pill px-1.5 text-xs font-semibold text-danger underline underline-offset-2 transition-colors hover:text-danger/80"
            >
              Remove document
            </button>
          </div>
        ) : null}
      </div>

      <p id={hintId} className="text-xs text-ink-faint">
        PDF, DOCX, or TXT up to {Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))} MB.
        Files are parsed in your browser only — never uploaded or saved.
      </p>

      <div aria-live="polite" className="min-h-5">
        {message ? (
          <p
            role={phase === 'error' ? 'alert' : 'status'}
            className={`text-sm font-medium ${MESSAGE_TONE[phase]}`}
          >
            {phase === 'extracting' ? <Spinner className="mr-2 align-middle" /> : null}
            {message}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function UploadGlyph({ busy }: { busy: boolean }) {
  return busy ? (
    <span
      aria-hidden="true"
      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent"
    />
  ) : (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className="h-4 w-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 13V3m0 0L6.5 6.5M10 3l3.5 3.5" />
      <path d="M3.5 13.5v1.75A1.75 1.75 0 0 0 5.25 17h9.5a1.75 1.75 0 0 0 1.75-1.75V13.5" />
    </svg>
  );
}

/** Small inline spinner reused across states. */
export function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent ${className}`}
    />
  );
}
